import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Row, Col, Alert, Spinner
} from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaPlus } from 'react-icons/fa';
import { pricingAPI } from '../../api/apiClient';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/css/CarPrice.css';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';

function Commissions() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState({
    from_amount: '',
    to_amount: '',
    commission_percentage: '',
    created_at: '',
  });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await pricingAPI.commissions.index({
        page,
        per_page: pagination.per_page
      });

      if (response.success) {
        setData(response.data.data || response.data);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.length
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_fetch_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_fetch_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openForm = (entry = null) => {
    if (entry) {
      setForm({
        from_amount: entry.from_amount || '',
        to_amount: entry.to_amount || '',
        commission_percentage: entry.commission_percentage || '',
        created_at: entry.created_at || '',
      });
      setEditData(entry);
    } else {
      setForm({
        from_amount: '',
        to_amount: '',
        commission_percentage: '',
        created_at: '',
      });
      setEditData(null);
    }
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        fd.append(key, value);
      });

      let response;
      if (editData) {
        response = await pricingAPI.commissions.update(editData.id, fd);
      } else {
        response = await pricingAPI.commissions.store(fd);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_saved_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowForm(false);
        fetchData(pagination.current_page); // Refresh current page
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_save_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_save_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error saving data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (id) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await pricingAPI.commissions.delete(deleteId);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_deleted_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowDelete(false);
        fetchData(pagination.current_page); // Refresh current page
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_delete_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_delete_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error deleting data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  return (
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="dashboard-title">💵{t('commissions')}</h3>
          <Button className='add-btn' onClick={() => openForm()}>
            <FaPlus className="me-1" /> {t('Neu_hinzufügen')}
          </Button>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : data.length === 0 ? (
          <Alert variant="info">{t('no_data_available')}</Alert>
        ) : (
          <div className="table-container shadow-sm rounded">
            <div className='table-responsive'>
              <Table bordered hover className="custom-table mb-0">
                <thead className="table-dark text-center">
                  <tr>
                    <th>#</th>
                    <th>{t("from")}</th>
                    <th>{t("to")}</th>
                    <th>{t("percentage")} %</th>
                    <th>{t("created_at")}</th>
                    <th>{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((entry, idx) => (
                    <tr key={entry.id} className="text-center">
                      <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                      <td>{entry.from_amount}</td>
                      <td>{entry.to_amount}</td>
                      <td>{entry.commission_percentage}</td>
                      <td>{entry.created_at}</td>
                      <td>
                        <Button
                          className="add-btn me-2"
                          size="sm"
                          onClick={() => openForm(entry)}
                          title="Bearbeiten"
                          disabled={loading}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setDeleteId(entry.id);
                            setShowDelete(true);
                          }}
                          title={t("Delete")}
                          disabled={loading}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        {!loading && pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchData}
            disabled={loading}
          />
        )}


      {/* Add/Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editData ? 'Bearbeiten' : 'Hinzufügen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3 gy-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("from")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.from_amount}
                    onChange={e => setForm(prev => ({ ...prev, from_amount: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("to")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.to_amount}
                    onChange={e => setForm(prev => ({ ...prev, to_amount: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("percentage")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.commission_percentage}
                    onChange={e => setForm(prev => ({ ...prev, commission_percentage: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Speichern'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_entry")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Löschen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Commissions;